import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTenantRoles } from "../../lib/api";
import { ACCESS_POLICIES, PERMISSIONS, levelOf, policyLabel } from "../../lib/access";
import { KNOWN_PERMISSIONS, PERMISSION_GROUPS, emptyRole } from "../../lib/organization";
import { useRoleForm } from "./useRoleForm";
import {
  FieldError,
  FieldLabel,
  GhostButton,
  PrimaryButton,
  SecondaryButton,
  inputClass,
} from "./primitives";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 018 0v4" />
    </svg>
  );
}

function RoleDrawer({ role, tenantId, onClose }) {
  const { form, set, errors, isNew, codeLocked, setLabel, setCode, toggle, setLevel, onSave, onDelete, pending } =
    useRoleForm(role, tenantId, onClose);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-[rgba(17,24,39,0.28)] [animation:isd-fade_120ms_ease-out]" onClick={onClose} />
      <aside
        role="dialog"
        aria-label={isNew ? "New role" : `Edit ${role.label}`}
        className="relative flex h-full w-full max-w-[520px] flex-col bg-white shadow-[0_0_0_1px_rgba(16,24,40,0.08),-6px_0_20px_rgba(16,24,40,0.18)] [animation:isd-slide_160ms_ease-out]"
      >
        <div className="flex items-start justify-between gap-4 px-6 pb-5 pt-6">
          <div className="min-w-0">
            <h2 className="text-[20px] font-semibold leading-7 text-[#111827]">{isNew ? "New role" : role.label}</h2>
            {!isNew && (
              <div className="mt-1 text-[13px] text-[#6B7280]">{plural(role.user_count || 0, "person", "people")}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F6F8FB] hover:text-[#111827]"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="h-px bg-[#F0F1F3]" />

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="flex flex-col gap-5">
            <div>
              <FieldLabel>Role name</FieldLabel>
              <input
                type="text"
                className={inputClass}
                value={form.label}
                onChange={(e) => setLabel(e.target.value)}
              />
              <FieldError>{errors.label}</FieldError>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel>Short name</FieldLabel>
                <input
                  type="text"
                  className={inputClass}
                  value={form.short_label}
                  onChange={(e) => set({ short_label: e.target.value })}
                />
                <FieldError>{errors.short_label}</FieldError>
              </div>
              <div>
                <FieldLabel>Role code</FieldLabel>
                <div className="relative">
                  <input
                    type="text"
                    className={`${inputClass} ${codeLocked ? "cursor-not-allowed pr-9 text-[#6B7280]" : ""}`}
                    value={form.code}
                    disabled={codeLocked}
                    onChange={(e) => setCode(e.target.value)}
                  />
                  {codeLocked && (
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]">
                      <LockIcon />
                    </span>
                  )}
                </div>
                <FieldError>{errors.code}</FieldError>
              </div>
            </div>

            <div>
              <FieldLabel>Access policy</FieldLabel>
              <div className="flex flex-col gap-2" role="radiogroup" aria-label="Access policy">
                {ACCESS_POLICIES.map((p) => {
                  const on = form.access_policy === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => set({ access_policy: p.id })}
                      className="flex h-11 cursor-pointer items-center gap-3 rounded-[10px] border px-[14px] text-left text-[14px] font-medium"
                      style={{
                        borderColor: on ? "#2667B5" : "#E5E7EB",
                        background: on ? "#EFF5FC" : "#FFFFFF",
                        color: on ? "#1F5596" : "#111827",
                      }}
                    >
                      <span
                        className="flex h-4 w-4 flex-none items-center justify-center rounded-full border-2"
                        style={{ borderColor: on ? "#2667B5" : "#D1D5DB" }}
                      >
                        {on && <span className="h-2 w-2 rounded-full bg-[#2667B5]" />}
                      </span>
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <FieldLabel>Permissions</FieldLabel>
              {/* Grouped only for reading; each is still ticked on its own. */}
              {PERMISSION_GROUPS.map(({ label, items }) => (
                <div key={label || "_"} className={label ? "mt-4" : ""}>
                  {label && (
                    <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.06em] text-[#6B7280]">
                      {label}
                    </div>
                  )}
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {items.map((p) => {
                  if (p.levelled) {
                    const level = levelOf(form.permissions, p.id);
                    return (
                      <div
                        key={p.id}
                        // A level row carries a label and three buttons, so it
                        // takes the whole row rather than half of one.
                        className="flex h-11 items-center justify-between gap-3 rounded-[10px] border px-[14px] text-[14px] font-medium text-[#111827] sm:col-span-2"
                        style={{ borderColor: level ? "#BBD4F1" : "#E5E7EB", background: level ? "#F6F8FB" : "#FFFFFF" }}
                      >
                        <span className="truncate">{p.label}</span>
                        <span className="flex flex-none items-center gap-1">
                          {[
                            { key: false, label: "None" },
                            { key: "read", label: "Read" },
                            { key: "edit", label: "Edit" },
                          ].map((o) => (
                            <button
                              key={String(o.key)}
                              type="button"
                              aria-pressed={level === o.key}
                              onClick={() => setLevel(p.id, o.key)}
                              className="rounded-md px-2 py-1 text-[12px] font-medium"
                              style={
                                level === o.key
                                  ? { background: "#2667B5", color: "#FFFFFF" }
                                  : { background: "transparent", color: "#6B7280" }
                              }
                            >
                              {o.label}
                            </button>
                          ))}
                        </span>
                      </div>
                    );
                  }
                  const on = form.permissions.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      onClick={() => toggle(p.id)}
                      className="flex h-11 cursor-pointer items-center gap-3 rounded-[10px] border px-[14px] text-left text-[14px] font-medium text-[#111827]"
                      style={{ borderColor: on ? "#BBD4F1" : "#E5E7EB", background: on ? "#F6F8FB" : "#FFFFFF" }}
                    >
                      <span
                        className="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[5px] border"
                        style={{ borderColor: on ? "#2667B5" : "#D1D5DB", background: on ? "#2667B5" : "#FFFFFF" }}
                      >
                        {on && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                        )}
                      </span>
                      {p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <FieldError>{errors.permissions}</FieldError>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 px-6 py-4 shadow-[0_-1px_0_#F0F1F3]">
          <PrimaryButton onClick={onSave} disabled={pending}>
            {pending && <Loader2 className="animate-spin" size={14} />}
            {isNew ? "Create role" : "Save changes"}
          </PrimaryButton>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          {!isNew && !form.is_system && (
            <button
              type="button"
              onClick={onDelete}
              disabled={pending}
              className="ml-auto inline-flex h-[38px] cursor-pointer items-center rounded-lg px-3 text-[14px] font-medium text-[#DC2626] hover:bg-[#FEF2F2]"
            >
              Delete role
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}

export default function RolesEditor({ tenantId }) {
  const { data, isLoading } = useTenantRoles(tenantId);
  const [editing, setEditing] = useState(null);
  const roles = data?.rows || [];

  if (isLoading) {
    return (
      <div className="mt-7 flex items-center gap-2 text-[13px] text-[#6B7280]">
        <Loader2 className="animate-spin" size={15} /> Loading
      </div>
    );
  }

  return (
    <>
      <div className="mt-6 flex items-center justify-between gap-4 pb-[14px]">
        <span className="text-[13px] text-[#6B7280]">{plural(roles.length, "role", "roles")}</span>
        <PrimaryButton onClick={() => setEditing(emptyRole())}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          New role
        </PrimaryButton>
      </div>

      <div className="mt-1 flex flex-col gap-[10px]">
        {roles.map((r) => {
          const perms = (r.permissions || []).filter((p) => KNOWN_PERMISSIONS.has(p)).length;
          return (
            <div
              key={r.id}
              className="flex flex-wrap items-center gap-5 rounded-xl bg-white px-[18px] py-4 border border-[#EAECF0] shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
            >
              <div className="min-w-0 flex-[1_1_220px]">
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-semibold text-[#111827]">{r.label}</span>
                  {r.short_label && (
                    <span className="rounded-full border border-[#E5E7EB] px-[7px] py-[2px] text-[11px] font-semibold text-[#4B5563]">
                      {r.short_label}
                    </span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-1.5 text-[13px] text-[#6B7280]">
                  {r.code}
                  {r.is_system && (
                    <span className="text-[#9CA3AF]" aria-label="Built-in role">
                      <LockIcon />
                    </span>
                  )}
                </div>
              </div>
              <div className="min-w-0 flex-[1_1_200px]">
                <div className="text-[12px] font-medium text-[#6B7280]">Access</div>
                <div className="mt-[3px] text-[14px] text-[#111827]">{policyLabel(r.access_policy)}</div>
              </div>
              <div className="min-w-0 flex-[1_1_160px]">
                <div className="text-[12px] font-medium text-[#6B7280]">Permissions</div>
                <div className="mt-[3px] text-[14px] text-[#111827]">
                  {perms} of {PERMISSIONS.length}
                </div>
              </div>
              <div className="flex flex-none items-center gap-[10px]">
                <span className="text-[13px] text-[#6B7280]">{plural(r.user_count || 0, "person", "people")}</span>
                <SecondaryButton onClick={() => setEditing(r)}>Edit</SecondaryButton>
              </div>
            </div>
          );
        })}
      </div>

      {editing && <RoleDrawer key={editing.id || "new"} role={editing} tenantId={tenantId} onClose={() => setEditing(null)} />}
    </>
  );
}
